/**
 * @module plugins/musicfavourite/adapter/favouriteService
 *
 * Logic for getting/adding/removing favourite items.
 */
define(
    'plugins/musicfavourite/adapter/favouriteService',['jquery-1.9', '../user', './favouriteApi', './knownErrors'],
    function($, user, api, Errors, exports) {

        'use strict';

        function itemIsAnArtist(item) {
            return item && item.type && item.type === 'artist';
        }

        function itemIsAPlaylist(item) {
            return item && item.type && item.type === 'playlist';
        }

        function itemIsATrack(item) {
            return item && item.type && item.type.substring(0,5) === 'track';
        }

        function itemIsAProgramme(item) {
            return item && item.type && (item.type === 'programme' || item.type === 'tlec');
        }

        function verifyItemIsSupported(input) {
            var item = Array.isArray(input) ? input[0] : input;
            if (itemIsAnArtist(item) || itemIsAPlaylist(item) || itemIsATrack(item) || itemIsAProgramme(item)) {
                return $.Deferred().resolve();
            } else {
                return $.Deferred().reject(Errors.UnsupportedType);
            }
        }

        function getFavouriteItems(item) {
            return verifyItemIsSupported(item).then(api.get);
        }

        function addFavouriteItem(item) {
            return verifyItemIsSupported(item).then(api.add.bind(null, item));
        }

        function removeFavouriteItem(item) {
            return verifyItemIsSupported(item).then(api.remove.bind(null, item));
        }

        function addFeedback(item) {
            return verifyItemIsSupported(item).then(api.addFeedback.bind(null, item));
        }

        function removeFeedback(item) {
            return verifyItemIsSupported(item).then(api.removeFeedback.bind(null, item));
        }
        
        function getItemTrackId(id) {
            var trackId = id;
            var idIndex = id.indexOf("#");
            if (idIndex > 0) {
                 trackId = id.substring(0, idIndex);
            }
            return trackId;
        }


        /**
         * Finds an item in favourites.
         *
         * Added workaround to retrieve another track with the same title, see: PSP-1935
         *
         * @param  {Object}   item     Favourite item to search for.
         * @param  {Object}   favs     Favourite Map from PCL.
         * @param  {Function} callback Callback to call upon completion.
         */
        function findItem(item, favs, callback) {
            var found;

            if (typeof favs !== 'undefined') {

                found = favs.find(item) || undefined;
                if (found) {
                    found = item;
                }
            } else {
                return false;
            }

            if (!found && itemIsATrack(item)) { // Workaround for tracks

                var lowerCaseTitle = item.title.toLowerCase();
                var trackId = getItemTrackId(item.id);
                
                favs.each(function(fav) { // TODO: refactor to fail fast / filter method
                    if (itemIsATrack(fav) && lowerCaseTitle == fav.title.toLowerCase()) {
                        found = fav;
                    } else if (itemIsATrack(fav) && trackId == getItemTrackId(fav.id) && (item.type == 'track' ||  item.type == 'track_chart' || item.type == 'track_playlist' || item.type == 'track_event' || item.type == 'track_guide' || item.type == 'track_segment' || item.type == 'track_offset')) {
                        found = fav;
                    }
                });
            }

            return callback && callback(found, favs) || found;
        }

        exports = {


            /**
             * Determines if the item is favourited or not.
             *
             * @param  {object}   item     Item spec to find, with an "appId", "type" and "id"
             * @param  {Function} callback Function callback which receives the result.
             *
             * @return {boolean|undefined} True when found, false otherwise. Fails gracefully on service error
             *                             and returns undefined, which can be useful for falsy checks.
             */
            'isFavourited': function(item, callback) { // Note: uses findItem() to determine if the item is favourited
                getFavouriteItems(item).then(function(result) {
                    callback(!!findItem(item, result));
                }, function() {
                    // Pass in undefined on error
                    callback(undefined);
                })
            },


            /**
             * Adds a favourite asynchronously.
             *
             * @param  {object}      item     Item specification of the favourite to add.
             * @param  {Function}    callback Function callback which receives the add result.
             */
            'add': function(item, callback) {
                addFavouriteItem(item).then(function(result) {
                    var adaptedResult = item;

                    // Add extra properties
                    if (result) {
                        if(result.trackName) {
                            adaptedResult.artistName = result.artistName;
                            adaptedResult.trackName = result.trackName;
                            adaptedResult.artistGid = result.artistGid;
                        }

                        if (result.imageTemplateUrl) {
                            adaptedResult.imageTemplateUrl = result.imageTemplateUrl;
                        }

                        // TODO MUSICCAT-2053: Remove this flagpole once transition is complete
                        adaptedResult.flagpoleRadioProfileConfirmation = Boolean(result.flagpoleRadioProfileConfirmation);
                    }

                    callback(adaptedResult);
                }, function(error) {
                    callback(error);
                });
            },


            /**
             * Removes a favourite asynchronously.
             *
             * @param  {object}      item     Item specification of the favourite to remove.
             * @param  {Function}    callback Function callback which receives the remove result.
             */
            'remove': function(item, callback) {
                getFavouriteItems(item).then(function(favs) {
                    findItem(item, favs, function(item) {
                        removeFavouriteItem(item).then(function(result) {
                            callback(result);
                        });
                    });
                });
            },

            /**
             * Adds a feedback item asynchronously.
             *
             * @param  {object}      item     Item specification of the feedback to add.
             * @param  {Function}    callback Function callback which receives the result.
             */
            'addFeedback': function(item, callback) {
                addFeedback(item).then(callback, callback);
            },

            /**
             * Removes a feedback item asynchronously.
             *
             * @param  {object}      item     Item specification of the feedback to remove.
             * @param  {Function}    callback Function callback which receives the result.
             */
            'removeFeedback': function(item, callback) {
                removeFeedback(item).then(callback, callback);
            },

            // Convenience method wrapping the User method
            'isSignedIn': function() {
                return user.isSignedIn();
            },

            // Allow overriding of the API module for stubbing
            'setApiModule': function(override) {
                api = override;
            },

            // Allow overriding of the User module for stubbing
            'setUserModule': function(override) {
                user = override;
            }
        };


        // Use singleton errors defined for known error states
        exports.errors = Errors;

        return exports;
    }
);

